import React, { FC, useMemo } from 'react';
import { getRefrigerantBandDisplay, NOT_BANDED_DISPLAY } from '../../utils/RefrigerantUtils';

interface RefrigerantLeakRateBarProps {
	/** Trailing-12-month leak rate, already rounded to 1 dp by the server. */
	leakRatePercent?: number | string;
	/** The threshold this appliance is banded against. Absent means "log it, do not band it". */
	thresholdPercent?: number | string;
	/** `Compliant` | `Watch` | `Exceeded`, or absent — decides the fill colour. */
	band?: string;
	height?: number;
}

/**
 * Where the threshold marker sits along the track, as a fraction.
 *
 * FIXED rather than proportional, and that is the whole design of this bar: the threshold is the only
 * number a reader is comparing against, so it has to land at the same x on every row or the rows stop
 * being comparable at a glance. A rate at exactly its threshold therefore always fills to the marker,
 * whether the threshold is 10, 20 or 30 per cent.
 */
const THRESHOLD_MARKER_POSITION = 0.7;

/**
 * The horizontal leak-rate meter from the design — a filled track with the statutory threshold marked
 * on it.
 *
 * Nothing here is recomputed: `leakRatePercent` and `band` both arrive already derived from the
 * append-only ledger. The bar only decides how far along the track the number sits, so it can never
 * disagree with the pill next to it.
 */
export const RefrigerantLeakRateBar: FC<RefrigerantLeakRateBarProps> = ({
	leakRatePercent,
	thresholdPercent,
	band,
	height = 8,
}) => {
	const numericRate = Number(leakRatePercent);
	const numericThreshold = Number(thresholdPercent);
	const hasRate =
		leakRatePercent !== null && leakRatePercent !== undefined && !Number.isNaN(numericRate);
	const hasThreshold =
		thresholdPercent !== null &&
		thresholdPercent !== undefined &&
		!Number.isNaN(numericThreshold) &&
		numericThreshold > 0;

	const fillFraction = useMemo(() => {
		if (!hasRate) return 0;
		// With no threshold there is nothing to scale against, so the track only has to show that
		// something was logged — a fixed sliver, not a proportion of a number that does not exist.
		if (!hasThreshold) return numericRate > 0 ? 0.08 : 0;
		return Math.min((numericRate / numericThreshold) * THRESHOLD_MARKER_POSITION, 1);
	}, [hasRate, hasThreshold, numericRate, numericThreshold]);

	const fillColor = hasThreshold
		? getRefrigerantBandDisplay(band).barColor
		: NOT_BANDED_DISPLAY.barColor;

	return (
		<div className="relative w-full">
			<div className="w-full overflow-hidden rounded bg-grey-4" style={{ height }}>
				<div
					style={{ width: `${fillFraction * 100}%`, height: '100%', backgroundColor: fillColor }}
				/>
			</div>
			{hasThreshold ? (
				<div
					className="absolute top-0"
					// 2px wide and pulled back by half its own width so the marker's centre, not its left
					// edge, is what sits on the threshold.
					style={{
						left: `calc(${THRESHOLD_MARKER_POSITION * 100}% - 1px)`,
						width: 2,
						height,
						backgroundColor: 'rgba(0,0,0,0.85)',
					}}
				/>
			) : null}
		</div>
	);
};

export default RefrigerantLeakRateBar;
